import {
  AnalysisBatchClientType,
  AnalysisStatus,
  ENTITY,
  UserActions,
} from '@/enums';
import { useQueryNavigate, useToggleModal } from '@/hooks';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { AnalysisParams } from '@/interfaces';
import { AnalysisEntity } from '@/interfaces/analysis';
import { useDeleteCommercialAnalysisBatch } from '@/queries';
import {
  CustomButton,
  CustomDropdown,
  Icon,
  InformativeModal,
} from '@amili-v3/amili-v3-design';
import { MenuProps } from 'antd';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import UpdateAnalysisBatchModal from '../sections/UpdateAnalysisBatchModal';
import { useAnalysisMutation } from './useAnalysisMutation';

const statusesAllowDeleteBatch = [
  AnalysisStatus.TO_BE_ANALYSED,
  AnalysisStatus.CAUTION,
];

type AnalysisDropdownProps = {
  analysisRecord: AnalysisEntity;
  batchClientType: AnalysisBatchClientType;
};

const AnalysisDropdown = (props: AnalysisDropdownProps) => {
  const { analysisRecord, batchClientType } = props;
  const { t } = useTranslation();
  const handleNavigate = useQueryNavigate<AnalysisParams>();

  const isResearchAnalysisBatch =
    batchClientType === AnalysisBatchClientType.RESEARCH;

  const {
    canUpdateAnalysis: canUpdateResearch,
    canDeleteAnalysis: canDeleteResearch,
  } = useCheckScopeCombinations([
    [{ entity: ENTITY.ANALYSIS, action: UserActions.UPDATE }],
    [{ entity: ENTITY.ANALYSIS, action: UserActions.DELETE }],
  ]);
  const {
    canUpdateCommercialAnalysisBatch: canUpdateCommercial,
    canDeleteCommercialAnalysisBatch: canDeleteCommercial,
  } = useCheckScopeCombinations([
    [{ entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH, action: UserActions.UPDATE }],
    [{ entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH, action: UserActions.DELETE }],
  ]);
  const { updateAnalysisModal } = useToggleModal(['updateAnalysis']);
  const { confirmDeleteModal } = useToggleModal(['confirmDelete']);

  const {
    mutationRerun,
    removeAnalysis: deleteResearchAnalysis,
    rerun,
    setRerun,
  } = useAnalysisMutation({
    handleNavigate,
  });
  const {
    mutate: deleteCommercialMutate,
    isPending: isPendingDeleteCommercial,
  } = useDeleteCommercialAnalysisBatch({
    onSuccess: confirmDeleteModal.toggle,
  });

  const handleDelete = () => {
    if (isResearchAnalysisBatch) {
      deleteResearchAnalysis.mutate(analysisRecord.id);
    } else {
      deleteCommercialMutate(analysisRecord.id);
    }
  };

  const dropdownItems: MenuProps['items'] = useMemo(() => {
    const allowDeleteBatch = isResearchAnalysisBatch
      ? canDeleteResearch
      : canDeleteCommercial;
    const isToBeAnalysisBatchStatus =
      analysisRecord.status === AnalysisStatus.TO_BE_ANALYSED;
    const isFailedBatchStatus =
      analysisRecord.status === AnalysisStatus.CAUTION;

    const allowedUpdateBatch = isResearchAnalysisBatch
      ? canUpdateResearch
      : canUpdateCommercial;
    const items: MenuProps['items'] = [
      allowedUpdateBatch
        ? {
            key: 'edit',
            label: t('common.edit'),
            onClick: updateAnalysisModal.toggle,
            icon: <Icon name="pen" color="var(--primary)" />,
            disabled: !isToBeAnalysisBatchStatus,
          }
        : null,
      isFailedBatchStatus
        ? {
            label: t('analysis.rerun'),
            key: 'rerun',
            onClick: () => setRerun(analysisRecord),
            icon: <Icon name="refresh" color="var(--primary)" />,
          }
        : null,
      allowDeleteBatch
        ? {
            label: t('common.delete'),
            key: 'delete',
            onClick: confirmDeleteModal.toggle,
            icon: <Icon name="trash" color="var(--primary)" />,
            disabled: !statusesAllowDeleteBatch.includes(
              analysisRecord.status as AnalysisStatus,
            ),
          }
        : null,
    ];
    return items.filter(Boolean);
  }, [
    analysisRecord,
    canDeleteCommercial,
    canDeleteResearch,
    canUpdateCommercial,
    canUpdateResearch,
    confirmDeleteModal.toggle,
    isResearchAnalysisBatch,
    setRerun,
    t,
    updateAnalysisModal.toggle,
  ]);

  return (
    <>
      {!isEmpty(dropdownItems) && (
        <CustomDropdown menu={{ items: dropdownItems }}>
          <div>
            <CustomButton type="white" prefix={<Icon name="moreVertical" />} />
          </div>
        </CustomDropdown>
      )}
      <InformativeModal
        centered
        open={!isEmpty(rerun)}
        layout="vertical"
        onCancel={() => setRerun({} as AnalysisEntity)}
        title={t('analysis.rerunAnalysis')}
        footer={[
          <CustomButton
            size="large"
            width="100%"
            key="confirm"
            onClick={() => mutationRerun.mutateAsync(analysisRecord.id)}
            loading={mutationRerun.isPending}
          >
            {t('common.confirm')}
          </CustomButton>,
        ]}
      >
        {t('analysis.confirmRerun', { displayId: analysisRecord?.displayId })}
      </InformativeModal>

      <UpdateAnalysisBatchModal
        analysisRecord={analysisRecord}
        isOpen={updateAnalysisModal.visible}
        toggleModal={updateAnalysisModal.toggle}
      />

      <InformativeModal
        error
        centered
        open={confirmDeleteModal.visible}
        layout="vertical"
        onCancel={confirmDeleteModal.toggle}
        title={t('common.confirmDeleteWord')}
        icon={<Icon name="trash" width={30} />}
        footer={
          <CustomButton
            size="large"
            width="100%"
            key="confirm"
            onClick={handleDelete}
            loading={
              isPendingDeleteCommercial || deleteResearchAnalysis.isPending
            }
          >
            {t('common.confirm')}
          </CustomButton>
        }
      >
        {t('common.confirmDeleteQuestion', {
          object: `Analysis Batch ID: ${analysisRecord?.displayId?.toUpperCase()}`,
        })}
      </InformativeModal>
    </>
  );
};
export default AnalysisDropdown;
